import { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, auth } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.jsx';

// Unified People page — replaces the separate Responders + Requesters pages.
// All users (administrators / responders / stakeholders / requesters) live here
// with role as an assignable column. Tab labels adapt to vendor.operatingMode.

const ROLE_LABEL = {
  vendor_admin: 'Administrator',
  responder: 'Responder',
  stakeholder: 'Stakeholder',
  customer: 'Requester',
};
const INTERNAL_ROLES = new Set(['vendor_admin', 'responder', 'stakeholder']);

export default function People() {
  const { claims } = useAuth();
  const vendorId = claims?.vendor_id;
  const currentUid = auth.currentUser?.uid;

  const [vendor, setVendor] = useState(null);
  const [users, setUsers] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'internal' | 'requesters'
  const [showInvite, setShowInvite] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!vendorId) return;
    return onSnapshot(doc(db, 'vendors', vendorId), (s) => setVendor(s.data() || {}));
  }, [vendorId]);

  useEffect(() => {
    if (!vendorId) return;
    return onSnapshot(
      collection(db, 'vendors', vendorId, 'users'),
      (snap) => setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((u) => !u.deletedAt)),
      (err) => setError(err.message),
    );
  }, [vendorId]);

  // Service vendors call their non-internal users "Customers"; internal-ops
  // shops call them "Staff" (the company's own employees reporting breakdowns).
  const mode = vendor?.operatingMode || 'service';
  const requesterLabel = mode === 'internal' ? 'Staff' : 'Customers';

  const visible = useMemo(() => {
    const all = users || [];
    if (filter === 'internal') return all.filter((u) => INTERNAL_ROLES.has(u.role));
    if (filter === 'requesters') return all.filter((u) => u.role === 'customer');
    return all;
  }, [users, filter]);

  async function handleRoleChange(u, newRole) {
    if (u.role === newRole) return;
    const fromLbl = ROLE_LABEL[u.role] || u.role;
    const toLbl = ROLE_LABEL[newRole] || newRole;
    // Crossing the internal ↔ requester boundary is a significant permission shift.
    const crossingBoundary = INTERNAL_ROLES.has(u.role) !== INTERNAL_ROLES.has(newRole);
    const msg = crossingBoundary
      ? `Change ${u.fullName || u.email} from ${fromLbl} to ${toLbl}?\n\nThis is a major change — their app surface and permissions shift significantly. Continue?`
      : `Change ${u.fullName || u.email} from ${fromLbl} to ${toLbl}? Takes effect immediately.`;
    if (!confirm(msg)) return;
    try {
      await httpsCallable(functions, 'setUserRole')({ uid: u.id, role: newRole });
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleRemove(u) {
    if (!confirm(`Remove ${u.fullName || u.email}? They lose access immediately.`)) return;
    try {
      await httpsCallable(functions, 'removeResponder')({ uid: u.id });
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0 }}>People</h2>
        <button onClick={() => setShowInvite(true)} className="btn-primary">+ Invite person</button>
      </div>
      <p style={{ color: 'var(--muted)', marginTop: -4 }}>
        Everyone on your account — administrators, responders, stakeholders, and {requesterLabel.toLowerCase()}.
        Change a role anytime from the dropdown.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button onClick={() => setFilter('all')} className={filter === 'all' ? 'btn-chip-active' : 'btn-chip'}>All</button>
        <button onClick={() => setFilter('internal')} className={filter === 'internal' ? 'btn-chip-active' : 'btn-chip'}>Internal team</button>
        <button onClick={() => setFilter('requesters')} className={filter === 'requesters' ? 'btn-chip-active' : 'btn-chip'}>{requesterLabel}</button>
      </div>

      {error && <div className="error">{error}</div>}

      {users === null && <div className="placeholder">Loading…</div>}
      {users && visible.length === 0 && (
        <div className="placeholder">No people in this view yet.</div>
      )}

      {users && visible.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((u) => (
              <tr key={u.id}>
                <td>{u.fullName || <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                <td>{u.email}</td>
                <td>
                  {u.phone
                    ? <code>{u.phone}</code>
                    : <span style={{ color: 'var(--muted)' }}>—</span>}
                </td>
                <td>
                  {u.id === currentUid ? (
                    <span className="badge">{ROLE_LABEL[u.role] || u.role}</span>
                  ) : (
                    <select
                      value={u.role}
                      onChange={(e) => handleRoleChange(u, e.target.value)}
                      style={{ fontSize: 13, padding: '4px 6px' }}
                    >
                      <option value="vendor_admin">Administrator</option>
                      <option value="responder">Responder</option>
                      <option value="stakeholder">Stakeholder</option>
                      <option value="customer">Requester</option>
                    </select>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {u.id !== currentUid && u.role !== 'vendor_admin' && (
                    <button onClick={() => handleRemove(u)} className="btn-ghost-danger">Remove</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showInvite && (
        <InviteModal
          mode={mode}
          onClose={() => setShowInvite(false)}
          onInvited={(info) => { setShowInvite(false); setInviteResult(info); }}
        />
      )}
      {inviteResult && (
        <InviteResultModal info={inviteResult} onClose={() => setInviteResult(null)} />
      )}
    </>
  );
}

// ----------------------------------------------------------------------------
// Invite modal — picks role first, then shows the right fields. Internal-team
// roles use inviteResponder (temp password); requester uses inviteCustomer
// (email link to self-set password).
// ----------------------------------------------------------------------------
function InviteModal({ mode, onClose, onInvited }) {
  const requesterLabel = mode === 'internal' ? 'Staff member' : 'Requester (customer)';
  const [role, setRole] = useState('responder');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [siteName, setSiteName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const isInternal = role !== 'customer';

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      if (isInternal) {
        const result = await httpsCallable(functions, 'inviteResponder')({
          email: email.trim(),
          fullName: fullName.trim(),
          role,
          ...(phone.trim() ? { phone: phone.trim() } : {}),
        });
        onInvited({
          kind: 'internal',
          email: email.trim(),
          fullName: fullName.trim(),
          role,
          tempPassword: result.data.tempPassword,
        });
      } else {
        await httpsCallable(functions, 'inviteCustomer')({
          email: email.trim(),
          ...(siteName.trim() ? { siteName: siteName.trim() } : {}),
        });
        onInvited({
          kind: 'customer',
          email: email.trim(),
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Invite person</h3>

        <label>Role</label>
        <select value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="responder">Responder — takes tickets</option>
          <option value="vendor_admin">Administrator — full access</option>
          <option value="stakeholder">Stakeholder — notified only (+$3/mo)</option>
          <option value="customer">{requesterLabel} — reports issues</option>
        </select>

        <label>Email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />

        {isInternal && (
          <>
            <label>Full name</label>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} required />

            <label>Mobile phone (optional, recommended)</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="0412 345 678"
              autoComplete="tel"
            />
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: -8, marginBottom: 8 }}>
              For SMS + voice fallback when push doesn't reach them.
            </div>
          </>
        )}

        {!isInternal && (
          <>
            <label>Site / location (optional)</label>
            <input
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
              placeholder="e.g. Bondi Junction store"
            />
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: -8, marginBottom: 8 }}>
              {mode === 'internal'
                ? 'Where this person works — used to label their tickets.'
                : "The customer's site — used to label their tickets."}
              <br />They'll receive an invitation email and set their own name + password.
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary" style={{ flex: 1 }}>
            {busy ? 'Inviting…' : 'Send invite'}
          </button>
        </div>
        {error && <div className="error">{error}</div>}
      </form>
    </div>
  );
}

// Shows either the temp password (internal) or a "check email" notice (customer).
function InviteResultModal({ info, onClose }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        {info.kind === 'internal' ? (
          <>
            <h3>Share these credentials</h3>
            <p style={{ color: 'var(--muted)' }}>
              {info.fullName} can sign in with the temp password below — they should reset it after first login.
              You'll see this only once.
            </p>
            <div className="kv">
              <div><strong>Email</strong></div>
              <div><code>{info.email}</code></div>
              <div><strong>Role</strong></div>
              <div>{ROLE_LABEL[info.role] || info.role}</div>
              <div><strong>Temp password</strong></div>
              <div><code>{info.tempPassword}</code></div>
            </div>
          </>
        ) : (
          <>
            <h3>Invitation sent</h3>
            <p style={{ color: 'var(--muted)' }}>
              An invitation email has been queued for <code>{info.email}</code>.
              They'll click the link, set their own password, and land in the requester app.
            </p>
          </>
        )}
        <button onClick={onClose} className="btn-primary" style={{ width: '100%', marginTop: 16 }}>Done</button>
      </div>
    </div>
  );
}
